import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInfoWidgetService } from '../../../services/info-widget-service';
import { IInfoWidget, typeOptions } from '../../../shared/IInfoWidget';
import { ECommonValue, EInputType, IInputField } from '../../ui/Input/Input';
import { useInputs } from '../../ui/Input/useInputs';
import { QueryKey } from '../../../services/query-keys';
import { Route } from '../../../routes';
import FormButtons from '../../ui/FormButtons/FormButtons';
import ErrorsAlert from '../../ErrorsAlert/ErrorsAlert';
import { ErrorResponse } from '../../../custom-fetch';

interface IInfoWidgetEditProps {
  id: string;
  widget?: IInfoWidget;
}

enum EInputs {
  type = "type",
  title = "title",
  content = "content",
  isVisible = "isVisible",
}

const InfoWidgetEdit: React.FC<IInfoWidgetEditProps> = ({ id, widget }) => {
  const isEdit = id !== "add";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { saveInfoWidget, deleteInfoWidget } = useInfoWidgetService();

  const { mutate, isPending, isError, error } = useMutation<
    IInfoWidget,
    ErrorResponse,
    IInfoWidget
  >({
    mutationFn: (data: IInfoWidget) => saveInfoWidget(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.infoWidgets] });
      navigate(Route.infoWidgets);
    },
  });

  const {
    mutate: deleteMutate,
    isPending: isDeleting,
    isError: isDeletingError,
    error: deletingError,
  } = useMutation<boolean, ErrorResponse>({
    mutationFn: () => deleteInfoWidget(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.infoWidgets] });
      navigate(Route.infoWidgets);
    },
  });


  const [inputs, setInputs] = useState<IInputField>({
    [EInputs.type]: {
      type: EInputType.reactSelect,
      label: "Widget Type",
      value: "",
      options: typeOptions,
      validation: { required: true },
    },
    [EInputs.title]: {
      type: EInputType.text,
      label: "Title",
      value: "",
    },
    [EInputs.isVisible]: {
      type: EInputType.checkbox,
      label: "Is Visible",
      value: [ECommonValue.YES],
      options: [{ value: ECommonValue.YES, label: "" }],
    },
    [EInputs.content]: {
      type: EInputType.wysiwyg,
      label: "Content",
      value: "",
    },
  });

  const formattedData = useMemo(() => {
    if (!widget) return undefined;
    return {
      ...widget,
      isVisible: widget.isVisible ? [ECommonValue.YES] : [],
    };
  }, [widget]);

  const { createInput, submit } = useInputs({
    data: formattedData,
    inputs,
    setInputs,
  });

  const submitHandler = async () => {
    const data = await submit();
    if (data) {
      const formattedSubmitData = {
        ...data,
        id: isEdit ? id : undefined,
        ordering: widget?.ordering || 0,
        isVisible: !!(data.isVisible && (data.isVisible as string[]).indexOf(ECommonValue.YES) !== -1),
      } as IInfoWidget;
      mutate(formattedSubmitData);
    }
  };

  const deleteHandler = () => {
    if (window.confirm("Are you sure you want to delete this widget?")) {
      deleteMutate();
    }
  };

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
        {createInput(EInputs.type)}
        {createInput(EInputs.title)}
      </div>
      {createInput(EInputs.isVisible)}
      {createInput(EInputs.content)}

      {isError && error && <ErrorsAlert errors={error.messages} />}
      {isDeletingError && deletingError && <ErrorsAlert errors={deletingError.messages} />}

      <FormButtons
        onSubmit={submitHandler}
        isLoading={isPending}
        onDelete={isEdit ? deleteHandler : undefined}
        isDeleting={isDeleting}
        deleteConfirmMessage="Are you sure you want to delete this widget?"
      />
    </>
  );
};

export default InfoWidgetEdit;
